/**
 * SafeColors — vendor color contrast normalizer.
 *
 * Vendors can pick any primary/secondary color for their campaign, and
 * many of those choices will be objectively unreadable when overlaid on
 * cream, white, or black surfaces (yellow-on-white is the classic
 * offender). This utility runs once at render-time and produces a
 * palette that:
 *   • preserves the VENDOR'S intent as closely as possible
 *   • never returns text/background pairs below WCAG AA contrast
 *   • never lets a vendor override CityDooh gold (#F5A800) — that
 *     remains a platform signature applied on top of any theme
 *   • falls back to the category theme's default palette when the
 *     vendor's choice fails contrast checks
 *
 * All math is per-frame cheap (a handful of arithmetic ops on 3 ints).
 */

export const CITYDOOH_GOLD = "#F5A800";
export const CITYDOOH_INK = "#0A0A0A";

/* ── Hex ↔ RGB helpers ─────────────────────────────────────────── */

/** Parse `#rgb`, `#rrggbb`, or `rgb(r,g,b)` → {r,g,b} 0..255. Null on bad input. */
export function parseHex(hex) {
  if (!hex) return null;
  const s = String(hex).trim();
  const m3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(s);
  if (m3) return {
    r: parseInt(m3[1] + m3[1], 16),
    g: parseInt(m3[2] + m3[2], 16),
    b: parseInt(m3[3] + m3[3], 16),
  };
  const m6 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(s);
  if (m6) return {
    r: parseInt(m6[1], 16),
    g: parseInt(m6[2], 16),
    b: parseInt(m6[3], 16),
  };
  const mr = /^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i.exec(s);
  if (mr) return { r: +mr[1], g: +mr[2], b: +mr[3] };
  return null;
}

/** {r,g,b} → `#rrggbb`. */
export function toHex({ r, g, b }) {
  const cx = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${cx(r)}${cx(g)}${cx(b)}`;
}

/* ── Perceptual math ───────────────────────────────────────────── */

/** WCAG relative luminance (0..1) for an sRGB color. */
export function luminance(rgb) {
  const c = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * c(rgb.r) + 0.7152 * c(rgb.g) + 0.0722 * c(rgb.b);
}

/** WCAG contrast ratio 1..21 between two colors. */
export function contrast(hexA, hexB) {
  const a = parseHex(hexA);
  const b = parseHex(hexB);
  if (!a || !b) return 1;
  const la = luminance(a);
  const lb = luminance(b);
  const lo = Math.min(la, lb);
  const hi = Math.max(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/** True when contrast is at least WCAG AA (large text = 3, body = 4.5). */
export function meetsAA(hexA, hexB, { large = false } = {}) {
  return contrast(hexA, hexB) >= (large ? 3.0 : 4.5);
}

/* ── HSL for tone-shifting ─────────────────────────────────────── */

function rgbToHsl({ r, g, b }) {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  switch (max) {
    case R: h = (G - B) / d + (G < B ? 6 : 0); break;
    case G: h = (B - R) / d + 2; break;
    default: h = (R - G) / d + 4;
  }
  return { h: h * 60, s, l };
}

function hslToRgb({ h, s, l }) {
  const H = ((h % 360) + 360) % 360 / 60;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((H % 2) - 1));
  const m = l - c / 2;
  const [R, G, B] =
    H < 1 ? [c, x, 0] :
    H < 2 ? [x, c, 0] :
    H < 3 ? [0, c, x] :
    H < 4 ? [0, x, c] :
    H < 5 ? [x, 0, c] : [c, 0, x];
  return { r: (R + m) * 255, g: (G + m) * 255, b: (B + m) * 255 };
}

/** Return a darker or lighter variant of `hex` by shifting HSL lightness. */
export function shade(hex, deltaL) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb);
  return toHex(hslToRgb({ ...hsl, l: Math.max(0, Math.min(1, hsl.l + deltaL)) }));
}

/** Clamp saturation to `maxS` so neon vendor picks don't burn eyes. */
export function desaturate(hex, maxS = 0.85) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb);
  if (hsl.s <= maxS) return hex;
  return toHex(hslToRgb({ ...hsl, s: maxS }));
}

/**
 * Ensure `hex` contrasts by at least `minContrast` against `bg`. Nudges
 * the color darker or lighter (whichever direction increases contrast)
 * in small steps until it passes, up to 20 iterations. Falls back to a
 * platform default if it never passes.
 */
export function ensureContrast(hex, bg, { minContrast = 4.5, fallback = CITYDOOH_INK } = {}) {
  if (!hex) return fallback;
  if (contrast(hex, bg) >= minContrast) return hex;
  const bgLum = luminance(parseHex(bg) || { r: 0, g: 0, b: 0 });
  const dir = bgLum > 0.5 ? -0.05 : +0.05;   // shift AWAY from bg lightness
  let out = hex;
  for (let i = 0; i < 20; i += 1) {
    out = shade(out, dir);
    if (contrast(out, bg) >= minContrast) return out;
  }
  return fallback;
}

/**
 * Main entry point — normalize a vendor's palette against the active
 * category theme + page surfaces. Returns a safe palette with:
 *   ink            — text color safe on cream & light surfaces
 *   primary        — hero/vendor accent (contrast-safe on cream)
 *   secondary      — supporting accent
 *   sliceA/sliceB  — alternating wheel-slice fills
 *   onSlice        — text color safe on top of sliceA/sliceB
 *   citydoohGold   — ALWAYS #F5A800 (platform signature, never overridden)
 *
 * `theme` is the fallback category palette from categoryThemes.js.
 */
export function normalizePalette({ primary, secondary, theme = {} }) {
  const BG_CREAM = "#F7F4EC";
  const BG_INK = "#0A0A0A";

  // 1. Clamp vendor color saturation to a sane maximum.
  const p0 = primary ? desaturate(primary, 0.9) : null;
  const s0 = secondary ? desaturate(secondary, 0.9) : null;

  // 2. Choose a "primary accent" that is legible on cream. When the
  //    vendor's primary is too pale (fails contrast on cream) we push
  //    it darker; when it's fine we leave it as-is; when it's missing
  //    we borrow from the category theme.
  const primaryOnCream = p0
    ? ensureContrast(p0, BG_CREAM, { minContrast: 3.5, fallback: theme.slicePrimary || CITYDOOH_INK })
    : (theme.slicePrimary || CITYDOOH_INK);

  const secondaryOnCream = s0
    ? ensureContrast(s0, BG_CREAM, { minContrast: 3.0, fallback: theme.sliceSecondary || primaryOnCream })
    : (theme.sliceSecondary || primaryOnCream);

  // 3. Alternating wheel slice colours — vendor primary + category
  //    secondary, both readable when a label sits on top.
  const sliceA = primaryOnCream;
  const sliceB = secondaryOnCream;

  // 4. Text color to use on top of the slices — black or white,
  //    whichever contrasts better against the darker of the two.
  const sliceBgForText = luminance(parseHex(sliceA) || { r: 0, g: 0, b: 0 }) >
                        luminance(parseHex(sliceB) || { r: 0, g: 0, b: 0 })
    ? sliceB : sliceA;
  const onSlice = contrast("#ffffff", sliceBgForText) > contrast("#000000", sliceBgForText)
    ? "#ffffff" : "#000000";

  return {
    ink: BG_INK,
    surfaceCream: BG_CREAM,
    surfaceInk: BG_INK,
    primary: primaryOnCream,
    secondary: secondaryOnCream,
    sliceA,
    sliceB,
    onSlice,
    citydoohGold: CITYDOOH_GOLD,   // platform-signature, immutable
  };
}
